import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PromoCodesSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function PromoCodesSubTab() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', discount_percent: '' });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/promo-codes`);
      setPromoCodes(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке промокодов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        discount_percent: parseFloat(formData.discount_percent)
      };
      
      if (editingPromoCode) {
        await axios.put(`${API_URL}/api/promo-codes/${editingPromoCode.id}`, payload);
        setEditingPromoCode(null);
      } else {
        await axios.post(`${API_URL}/api/promo-codes`, payload);
      }
      setShowForm(false);
      setFormData({ name: '', code: '', discount_percent: '' });
      fetchPromoCodes();
    } catch (err) {
      setError('Ошибка при сохранении промокода: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот промокод?')) {
      try {
        await axios.delete(`${API_URL}/api/promo-codes/${id}`);
        fetchPromoCodes();
      } catch (err) {
        setError('Ошибка при удалении промокода: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPromoCode(null);
    setFormData({ name: '', code: '', discount_percent: '' });
  };

  const handleEdit = (promoCode) => {
    setEditingPromoCode(promoCode);
    setFormData({
      name: promoCode.name,
      code: promoCode.code,
      discount_percent: promoCode.discount_percent.toString()
    });
    setShowForm(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="promo-codes-sub-tab">
      <div className="tab-header">
        <h2>Управление промокодами</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить промокод
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingPromoCode ? 'Редактировать промокод' : 'Добавить новый промокод'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Название промокода *</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                maxLength="255"
                placeholder="Введите название промокода"
              />
            </div>
            <div className="form-group">
              <label htmlFor="code">Код промокода (10 символов) *</label>
              <input
                type="text"
                id="code"
                value={formData.code}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
                  setFormData({ ...formData, code: value });
                }}
                required
                minLength="10"
                maxLength="10"
                placeholder="ABCD1234EF"
                pattern="[A-Z0-9]{10}"
              />
              <small className="form-hint">
                Только буквы и цифры, ровно 10 символов
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="discount_percent">Скидка (%) *</label>
              <input
                type="number"
                id="discount_percent"
                value={formData.discount_percent}
                onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                required
                min="0"
                max="100"
                step="0.01"
                placeholder="10.5"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingPromoCode ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Название</th>
              <th>Код</th>
              <th>Скидка (%)</th>
              <th>Дата создания</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {promoCodes.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  Нет промокодов. Добавьте первый промокод.
                </td>
              </tr>
            ) : (
              promoCodes.map((promoCode) => (
                <tr key={promoCode.id}>
                  <td>{promoCode.id}</td>
                  <td>{promoCode.name}</td>
                  <td><code className="promo-code-display">{promoCode.code}</code></td>
                  <td>{promoCode.discount_percent}%</td>
                  <td>{formatDate(promoCode.created_at)}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(promoCode)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(promoCode.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PromoCodesSubTab;
